import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  AppBar,
  Toolbar,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  ListItemText,
  Tooltip,
  FormHelperText,
  Stack,
  Chip,
  Menu,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemButton,
  ListItemText as MuiListItemText,
  Divider
} from '@mui/material';
import {
  GetApp as DownloadIcon,
  PictureAsPdf as PdfIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Logout as LogoutIcon,
  Event as EventIcon,
  Announcement as AnnouncementIcon,
  MonetizationOn as MonetizationOnIcon,
  Group as GroupIcon,
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  Menu as MenuIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import axios from 'axios';
import logo from '../assets/logo.png';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('dashboard');
  const navigationItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, id: 'dashboard' },
    { text: 'Members', icon: <PersonIcon />, id: 'members' },
    { text: 'Events', icon: <EventIcon />, id: 'events' },
    { text: 'Donations', icon: <MonetizationOnIcon />, id: 'donations' },
    { text: 'Announcements', icon: <AnnouncementIcon />, id: 'announcements' },
    { text: 'Finances', icon: <MonetizationOnIcon />, id: 'finances' },
  ];

  // Updated styles object with Apple's design system
  const styles = {
    container: {
      display: 'flex',
      minHeight: '100vh',
      backgroundColor: '#ffffff',
      flexDirection: { xs: 'column', md: 'row' },
    },
    sidebar: {
      width: { xs: '100%', md: '240px' },
      backgroundColor: '#ffffff',
      borderRight: { xs: 'none', md: '1px solid #d2d2d7' },
      borderBottom: { xs: '1px solid #d2d2d7', md: 'none' },
      padding: { xs: '12px', md: '24px' },
      display: 'flex',
      flexDirection: { xs: 'row', md: 'column' },
      position: { xs: 'relative', md: 'fixed' },
      top: 0,
      left: 0,
      bottom: { xs: 'auto', md: 0 },
      overflowY: 'auto',
      overflowX: { xs: 'auto', md: 'hidden' },
      boxShadow: 'none',
      zIndex: 1000,
    },
    sidebarItem: {
      display: 'flex',
      alignItems: 'center',
      padding: { xs: '8px', md: '12px 16px' },
      borderRadius: '8px',
      marginBottom: { xs: 0, md: '8px' },
      marginRight: { xs: '8px', md: 0 },
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      color: '#1d1d1f',
      whiteSpace: { xs: 'nowrap', md: 'normal' },
      fontFamily: '"SF Pro Text", -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
      fontSize: '15px',
      '&:hover': {
        backgroundColor: '#f5f5f7',
      },
      '&.active': {
        backgroundColor: '#f5f5f7',
        color: '#0066cc',
        fontWeight: 500,
      },
    },
    mainContent: {
      marginLeft: { xs: 0, md: '240px' },
      flex: 1,
      padding: { xs: '16px', md: '24px 32px' },
      width: { xs: '100%', md: 'calc(100% - 240px)' },
      boxSizing: 'border-box',
      marginTop: { xs: '16px', md: 0 },
    },
    sectionHeader: {
      padding: { xs: '16px', md: '24px' },
      marginBottom: { xs: '24px', md: '32px' },
      borderBottom: '1px solid #d2d2d7',
      '& h5': {
        fontSize: '24px',
        fontWeight: 600,
        color: '#1d1d1f',
        fontFamily: '"SF Pro Display", -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
        letterSpacing: '-0.015em',
        margin: 0
      }
    },
    card: {
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      border: '1px solid #d2d2d7',
      padding: '20px',
      marginBottom: '16px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
      },
    },
    statCard: {
      padding: '24px',
      borderRadius: '12px',
      backgroundColor: '#ffffff',
      border: '1px solid #d2d2d7',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      transition: 'all 0.3s ease-in-out',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
      },
    },
  };
  const [users, setUsers] = useState([]);
  const [donations, setDonations] = useState([]);
  const [spendings, setSpendings] = useState([]);
  const [spendingStats, setSpendingStats] = useState({
    totalSpent: 0,
    categoryTotals: {}
  });
  const [donationStats, setDonationStats] = useState({
    totalDonated: 0,
    totalPending: 0,
    verifiedCount: 0,
    pendingCount: 0,
    rejectedCount: 0,
    userTotals: {},
    monthlyTotals: {}
  });
  const [announcements, setAnnouncements] = useState([]);
  const [events, setEvents] = useState([]);
  const [openDialog, setOpenDialog] = useState('');
  const [formData, setFormData] = useState({});
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [openSpendingDialog, setOpenSpendingDialog] = useState(false);
  const [spendingForm, setSpendingForm] = useState({
    amount: '',
    description: '',
    category: '',
    date: new Date().toISOString().split('T')[0],
    reference_number: '',
    type: 'outflow',
    reason: ''
  });
  const [editAnnouncement, setEditAnnouncement] = useState(null);
  const [editEvent, setEditEvent] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [editingDonation, setEditingDonation] = useState(null);
  const [editingSpending, setEditingSpending] = useState(null);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [openDateRangeDialog, setOpenDateRangeDialog] = useState(false);
  const [selectedReportType, setSelectedReportType] = useState(null);
  const [financesSection, setFinancesSection] = useState('expenses');
  const [financeStats, setFinanceStats] = useState({ inflow: 0, outflow: 0 });

  useEffect(() => {
    fetchUsers();
    fetchAnnouncements();
    fetchEvents();
    fetchData();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Fetched users:', response.data);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
      setError('Failed to fetch users');
    }
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      // Fetch donations with statistics
      const donationsResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/donations/all`,
        config
      );
      setDonations(donationsResponse.data.donations);
      setDonationStats(donationsResponse.data.statistics);

      // Fetch spendings and statistics
      const spendingsResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/spendings`,
        config
      );
      setSpendings(spendingsResponse.data);
      // The new endpoint provides the full list, so we can calculate stats on the frontend
      // This part can be removed if the new /statistics endpoint is used, but for now, we adapt.
      const stats = spendingsResponse.data.reduce((acc, s) => {
        if (s.type === 'outflow') {
            acc.totalSpent += Number(s.amount);
            acc.categoryTotals[s.category] = (acc.categoryTotals[s.category] || 0) + Number(s.amount);
        }
        return acc;
      }, { totalSpent: 0, categoryTotals: {} });

      setSpendingStats(stats);

      // Calculate total inflow and outflow for the Finances tab
      const inflowTotal = spendingsResponse.data
        .filter(s => s.type === 'inflow')
        .reduce((sum, s) => sum + Number(s.amount), 0);
      
      const outflowTotal = spendingsResponse.data
        .filter(s => s.type === 'outflow')
        .reduce((sum, s) => sum + Number(s.amount), 0);

      setFinanceStats({ inflow: inflowTotal, outflow: outflowTotal });

    } catch (error) {
      console.error('Error fetching data:', error);
      setError(error.response?.data?.message || 'Failed to fetch data');
    }
  };

  const fetchAnnouncements = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/announcements`,
        config
      );
      setAnnouncements(response.data);
    } catch (error) {
      console.error('Error fetching announcements:', error);
      setError(error.response?.data?.message || 'Failed to fetch announcements');
    }
  };

  const fetchEvents = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/events`,
        config
      );
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
      setError(error.response?.data?.message || 'Failed to fetch events');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const handleDialogOpen = (dialogType) => {
    setOpenDialog(dialogType);
    setFormData({});
    setError('');
  };

  const handleDialogClose = () => {
    setOpenDialog('');
    setFormData({});
    setEditAnnouncement(null);
    setEditEvent(null);
    setEditingUser(null);
    setEditingDonation(null);
    setEditingSpending(null);
    setError('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    console.log(`Input changed - name: ${name}, value: ${value}`);
    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: value
      };
      console.log('Updated form data:', updated);
      return updated;
    });
  };

  const isAttendanceFormValid = () => {
    console.log('Checking attendance form validity:', formData);
    return formData.user_id && formData.date && formData.status;
  };

  const isEventFormValid = () => {
    console.log('Checking event form validity:', formData);
    return (
      formData.title?.trim() &&
      formData.description?.trim() &&
      formData.date &&
      formData.time &&
      formData.location?.trim()
    );
  };

  const handleSubmit = async () => {
    try {
      setError('');
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      let response;
      if (editAnnouncement) {
        response = await axios.put(
          `${process.env.REACT_APP_API_URL}/api/admin/announcements/${editAnnouncement.id}`,
          {
            ...formData,
            core_members: formData.visibility === 'core' ? formData.core_members : []
          },
          config
        );
        setAnnouncements(prev => 
          prev.map(a => a.id === editAnnouncement.id ? response.data : a)
        );
        setSuccessMessage('Announcement updated successfully');
      } else {
        response = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/admin/announcements`,
          {
            ...formData,
            core_members: formData.visibility === 'core' ? formData.core_members : []
          },
          config
        );
        setAnnouncements(prev => [response.data, ...prev]);
        setSuccessMessage('Announcement created successfully');
      }

      handleDialogClose();
    } catch (err) {
      console.error('Error submitting announcement:', err);
      setError(err.response?.data?.message || 'Failed to save announcement');
    }
  };

  const handleVerifyDonation = async (id, newStatus) => {
    try {
      setError('');
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/admin/donations/${id}/verify`,
        { status: newStatus },
        config
      );

      setDonations(prev => prev.map(d => d.id === id ? response.data : d));
      setSuccessMessage(`Donation ${newStatus === 'verified' ? 'approved' : 'rejected'} successfully`);
    } catch (err) {
      console.error('Error verifying donation:', err);
      setError(err.response?.data?.message || 'Failed to update donation status');
    }
  };

  const handleInitEditSpending = (spending) => {
    setSpendingForm({
      amount: spending.amount.toString(),
      description: spending.description,
      category: spending.category,
      date: spending.date,
      reference_number: spending.reference_number || '',
      type: spending.type || 'outflow',
      reason: spending.reason || ''
    });
    setEditingSpending(spending.id);
    setOpenDialog('spending');
  };

  const handleSubmitSpending = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      const spendingData = {
        amount: Number(spendingForm.amount),
        description: spendingForm.description.trim(),
        category: spendingForm.category.trim(),
        date: spendingForm.date,
        reference_number: spendingForm.reference_number?.trim() || '',
        type: spendingForm.type,
        reason: spendingForm.reason?.trim() || ''
      };

      if (editingSpending) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/spendings/${editingSpending}`,
          spendingData,
          config
        );
        setSuccessMessage('Record updated successfully');
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/spendings`,
          spendingData,
          config
        );
        setSuccessMessage('Record added successfully');
      }

      setOpenDialog('');
      setSpendingForm({
        amount: '',
        description: '',
        category: '',
        date: new Date().toISOString().split('T')[0],
        reference_number: '',
        type: 'outflow',
        reason: ''
      });
      setEditingSpending(null);
      await fetchData(); // Refresh data after adding/editing
    } catch (error) {
      console.error('Error handling spending:', error);
      setError(error.response?.data?.message || 'Failed to handle spending');
    }
  };

  const handleEditAnnouncement = (announcement) => {
    setEditAnnouncement(announcement);
    setFormData({
      title: announcement.title,
      content: announcement.content,
      visibility: announcement.visibility || 'all',
      core_members: announcement.core_members || []
    });
    setOpenDialog('announcement');
  };

  const handleDeleteAnnouncement = async (id) => {
    try {
      if (!window.confirm('Are you sure you want to delete this announcement?')) {
        return;
      }

      await axios.delete(`${process.env.REACT_APP_API_URL}/api/admin/announcements/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      setAnnouncements(prev => prev.filter(a => a.id !== id));
      setSuccessMessage('Announcement deleted successfully');
    } catch (err) {
      console.error('Error deleting announcement:', err);
      setError(err.response?.data?.message || 'Failed to delete announcement');
    }
  };

  const handleCreateEvent = () => {
    setFormData({
      title: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
      time: '09:00',
      location: ''
    });
    setEditEvent(null);
    setOpenDialog('event');
  };

  const handleEditEvent = (event) => {
    setEditEvent(event);
    setFormData({
      title: event.title,
      description: event.description,
      date: event.date,
      time: event.time,
      location: event.location
    });
    setOpenDialog('event');
  };

  const handleDeleteEvent = async (id) => {
    try {
      if (!window.confirm('Are you sure you want to delete this event?')) {
        return;
      }

      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/admin/events/${id}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      
      setEvents(prev => prev.filter(e => e.id !== id));
      setSuccessMessage('Event deleted successfully');
    } catch (err) {
      console.error('Error deleting event:', err);
      setError(err.response?.data?.message || 'Failed to delete event');
    }
  };

  const handleEventSubmit = async () => {
    try {
      setError('');
      let response;

      if (editEvent) {
        response = await axios.put(
          `${process.env.REACT_APP_API_URL}/api/admin/events/${editEvent.id}`,
          formData,
          {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          }
        );
        setEvents(prev => 
          prev.map(e => e.id === editEvent.id ? response.data : e)
        );
        setSuccessMessage('Event updated successfully');
      } else {
        response = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/admin/events`,
          formData,
          {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          }
        );
        setEvents(prev => [response.data, ...prev]);
        setSuccessMessage('Event created successfully');
      }

      handleDialogClose();
    } catch (err) {
      console.error('Error submitting event:', err);
      setError(err.response?.data?.message || 'Failed to save event');
    }
  };

  const handleEditUser = (user) => {
    setFormData({
      username: user.username,
      full_name: user.full_name,
      email: user.email,
      role: user.role
    });
    setEditingUser(user);
    setOpenDialog('user');
  };

  const handleDeleteUser = async (id) => {
    try {
      if (!window.confirm('Are you sure you want to delete this user?')) {
        return;
      }

      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/admin/users/${id}`,
        config
      );

      setUsers(prev => prev.filter(u => u.id !== id));
      setSuccessMessage('User deleted successfully');
    } catch (err) {
      console.error('Error deleting user:', err);
      setError(err.response?.data?.message || 'Failed to delete user');
    }
  };

  const handleUserSubmit = async () => {
    try {
      setError('');
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      let response;
      if (editingUser) {
        // Update existing user
        response = await axios.put(
          `${process.env.REACT_APP_API_URL}/api/admin/users/${editingUser.id}`,
          formData,
          config
        );
        setUsers(prev => prev.map(user => user.id === editingUser.id ? response.data : user));
        setSuccessMessage('User updated successfully');
      } else {
        // Create new user
        response = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/admin/users`,
          {
            ...formData,
            password: formData.password || 'ikshana123' // Default password if not provided
          },
          config
        );
        setUsers(prev => [response.data, ...prev]);
        setSuccessMessage('User created successfully');
      }

      handleDialogClose();
    } catch (err) {
      console.error('Error saving user:', err);
      setError(err.response?.data?.message || 'Failed to save user');
    }
  };

  const handleEditDonation = (donation) => {
    setFormData({
      amount: donation.amount,
      reference_number: donation.reference_number,
      status: donation.status,
      notes: donation.notes,
      username: donation.created_by
    });
    setEditingDonation(donation);
    setOpenDialog('donation');
  };

  const handleDeleteDonation = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/admin/donations/${id}`,
        config
      );

      setDonations(prev => prev.filter(d => d.id !== id));
      setSuccessMessage('Donation deleted successfully');
    } catch (error) {
      console.error('Error deleting donation:', error);
      setError(error.response?.data?.message || 'Failed to delete donation');
    }
  };

  const handleCreateDonation = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/admin/donations`,
        {
          ...formData,
          username: formData.username || 'Unknown'  // Add username of donation maker
        },
        config
      );

      setDonations(prev => [response.data, ...prev]);
      handleDialogClose();
      setSuccessMessage('Donation created successfully');
    } catch (error) {
      console.error('Error creating donation:', error);
      setError(error.response?.data?.message || 'Failed to create donation');
    }
  };

  const handleUpdateDonation = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/admin/donations/${editingDonation.id}`,
        formData,
        config
      );

      setDonations(prev => prev.map(d => d.id === editingDonation.id ? response.data : d));
      handleDialogClose();
      setSuccessMessage('Donation updated successfully');
    } catch (error) {
      console.error('Error updating donation:', error);
      setError(error.response?.data?.message || 'Failed to update donation');
    }
  };

  const isDonationFormValid = () => {
    return (
      formData.amount > 0 &&
      formData.reference_number?.trim() &&
      formData.status &&
      formData.username?.trim()
    );
  };

  const handleEditSpending = (spending) => {
    handleInitEditSpending(spending);
  };

  const handleDeleteSpending = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/spendings/${id}`,
        config
      );
      
      setSpendings(prev => prev.filter(s => s.id !== id));
      setSuccessMessage('Spending record deleted successfully');
    } catch (err) {
      console.error('Error deleting spending:', err);
      setError(err.response?.data?.message || 'Failed to delete spending record');
    }
  };

  const isSpendingFormValid = () => {
    return (
      spendingForm.amount > 0 &&
      spendingForm.description?.trim() &&
      spendingForm.category &&
      spendingForm.date
    );
  };

  const isUserFormValid = () => {
    return (
      formData.username?.trim() &&
      formData.full_name?.trim() &&
      formData.email?.trim() &&
      formData.role &&
      (!formData.password || formData.password.length >= 6)
    );
  };

  const isAnnouncementFormValid = () => {
    return (
      formData.title?.trim() &&
      formData.content?.trim() &&
      formData.visibility
    );
  };

  const handleGenerateReport = (reportType) => {
    setSelectedReportType(reportType);
    setDateRange({ start: '', end: '' });
    setOpenDateRangeDialog(true);
  };

  const handleDateRangeSubmit = () => {
    if (selectedReportType === 'donations') {
      generateDonationsPDF(dateRange);
    } else {
      generateSpendingsPDF(dateRange);
    }
    setOpenDateRangeDialog(false);
  };

  const generateDonationsPDF = (dateRange) => {
    setDownloadingReport(true);
    try {
      const doc = new jsPDF();
      
      // Filter donations based on date range
      const filteredDonations = donations.filter(donation => {
        const donationDate = new Date(donation.created_at);
        return donationDate >= new Date(dateRange.start) && donationDate <= new Date(dateRange.end);
      });

      // Calculate filtered statistics
      const filteredStats = {
        totalDonated: filteredDonations.reduce((sum, d) => sum + d.amount, 0),
        verifiedCount: filteredDonations.filter(d => d.status === 'verified').length,
        pendingCount: filteredDonations.filter(d => d.status === 'pending').length
      };
      
      // Add title
      doc.setFontSize(20);
      doc.text('Ikshana Foundation - Donations Report', 14, 22);
      doc.setFontSize(12);
      doc.text(`Period: ${new Date(dateRange.start).toLocaleDateString()} to ${new Date(dateRange.end).toLocaleDateString()}`, 14, 32);

      // Add statistics
      doc.setFontSize(14);
      doc.text('Donation Statistics', 14, 45);
      doc.setFontSize(12);
      const stats = [
        ['Total Donations:', `₹${filteredStats.totalDonated}`],
        ['Verified Donations:', filteredStats.verifiedCount],
        ['Pending Donations:', filteredStats.pendingCount],
        ['Average Donation:', `₹${(filteredStats.totalDonated / (filteredStats.verifiedCount + filteredStats.pendingCount) || 0).toFixed(2)}`]
      ];
      doc.autoTable({
        startY: 50,
        head: [['Metric', 'Value']],
        body: stats,
        theme: 'grid',
        headStyles: { fillColor: [63, 81, 181] }
      });

      // Add donations table
      if (filteredDonations.length > 0) {
        doc.addPage();
        doc.setFontSize(16);
        doc.text('Detailed Donations List', 14, 22);
        
        const donationsData = filteredDonations.map(donation => [
          new Date(donation.created_at).toLocaleDateString(),
          donation.user_name || 'N/A',
          `₹${donation.amount}`,
          donation.payment_method,
          donation.reference_number,
          donation.status,
          donation.description || 'N/A'
        ]);

        doc.autoTable({
          startY: 30,
          head: [['Date', 'Donor', 'Amount', 'Payment Method', 'Reference', 'Status', 'Description']],
          body: donationsData,
          theme: 'grid',
          headStyles: { fillColor: [63, 81, 181] },
          styles: { fontSize: 10 },
          columnStyles: {
            0: { cellWidth: 25 },
            2: { cellWidth: 20 },
            3: { cellWidth: 25 },
            5: { cellWidth: 20 }
          }
        });
      }

      doc.save(`ikshana-donations-report-${dateRange.start}-to-${dateRange.end}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      setError('Failed to generate PDF report');
    }
    setDownloadingReport(false);
    setDownloadMenuAnchor(null);
  };

  const generateSpendingsPDF = (dateRange) => {
    setDownloadingReport(true);
    try {
      const doc = new jsPDF();
      
      // Filter spendings based on date range
      const filteredSpendings = spendings.filter(spending => {
        const spendingDate = new Date(spending.date);
        return spendingDate >= new Date(dateRange.start) && spendingDate <= new Date(dateRange.end);
      });

      // Calculate filtered statistics
      const filteredStats = {
        totalSpent: filteredSpendings.reduce((sum, s) => sum + s.amount, 0),
        categoryTotals: filteredSpendings.reduce((acc, s) => {
          acc[s.category] = (acc[s.category] || 0) + s.amount;
          return acc;
        }, {})
      };
      
      // Add title
      doc.setFontSize(20);
      doc.text('Ikshana Foundation - Spendings Report', 14, 22);
      doc.setFontSize(12);
      doc.text(`Period: ${new Date(dateRange.start).toLocaleDateString()} to ${new Date(dateRange.end).toLocaleDateString()}`, 14, 32);

      // Add statistics
      doc.setFontSize(14);
      doc.text('Spending Statistics', 14, 45);
      doc.setFontSize(12);
      
      const categoryStats = Object.entries(filteredStats.categoryTotals).map(([category, amount]) => [
        category,
        `₹${amount}`
      ]);

      doc.autoTable({
        startY: 50,
        head: [['Category', 'Total Amount']],
        body: [
          ['Total Spent:', `₹${filteredStats.totalSpent}`],
          ...categoryStats
        ],
        theme: 'grid',
        headStyles: { fillColor: [63, 81, 181] }
      });

      // Add spendings table
      if (filteredSpendings.length > 0) {
        doc.addPage();
        doc.setFontSize(16);
        doc.text('Detailed Spendings List', 14, 22);
        
        const spendingsData = filteredSpendings.map(spending => [
          new Date(spending.date).toLocaleDateString(),
          spending.category,
          `₹${spending.amount}`,
          spending.description || 'N/A',
          spending.approved_by || 'N/A'
        ]);

        doc.autoTable({
          startY: 30,
          head: [['Date', 'Category', 'Amount', 'Description', 'Approved By']],
          body: spendingsData,
          theme: 'grid',
          headStyles: { fillColor: [63, 81, 181] },
          styles: { fontSize: 10 },
          columnStyles: {
            0: { cellWidth: 25 },
            2: { cellWidth: 20 }
          }
        });
      }

      doc.save(`ikshana-spendings-report-${dateRange.start}-to-${dateRange.end}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      setError('Failed to generate PDF report');
    }
    setDownloadingReport(false);
    setDownloadMenuAnchor(null);
  };

  const renderUsers = () => {
    return (
      <TableContainer component={Paper} sx={{ borderRadius: '12px', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Username</TableCell>
              <TableCell>Full Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.username}</TableCell>
                <TableCell>{user.full_name}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Chip
                    label={user.role}
                    color={user.role === 'admin' ? 'primary' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleEditUser(user)} size="small">
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDeleteUser(user.id)} size="small" color="error">
                    <LogoutIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderEvents = () => {
    return (
      <Grid container spacing={3}>
        {events.map((event) => (
          <Grid item xs={12} md={6} lg={4} key={event.id}>
            <Paper sx={styles.card}>
              <Typography variant="h6" gutterBottom>
                {event.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                {event.description}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Chip icon={<EventIcon />} label={new Date(event.date).toLocaleDateString()} size="small" />
                <Chip icon={<GroupIcon />} label={event.location} size="small" />
              </Stack>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button size="small" onClick={() => handleEditEvent(event)}>
                  Edit
                </Button>
                <Button size="small" color="error" onClick={() => handleDeleteEvent(event.id)}>
                  Delete
                </Button>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderDonations = () => {
    return (
      <TableContainer component={Paper} sx={{ borderRadius: '12px', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Amount</TableCell>
              <TableCell>Donor</TableCell>
              <TableCell>Reference</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {donations.map((donation) => (
              <TableRow key={donation.id}>
                <TableCell>₹{donation.amount}</TableCell>
                <TableCell>{donation.created_by}</TableCell>
                <TableCell>{donation.reference_number}</TableCell>
                <TableCell>
                  <Chip
                    label={donation.status}
                    color={
                      donation.status === 'verified' ? 'success' :
                      donation.status === 'rejected' ? 'error' :
                      'warning'
                    }
                    size="small"
                  />
                </TableCell>
                <TableCell>{new Date(donation.created_at).toLocaleDateString()}</TableCell>
                <TableCell>
                  <IconButton onClick={() => handleEditDonation(donation)} size="small">
                    <EditIcon />
                  </IconButton>
                  {donation.status === 'pending' && (
                    <>
                      <IconButton
                        onClick={() => handleVerifyDonation(donation.id, 'verified')}
                        size="small"
                        color="success"
                      >
                        <DashboardIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleVerifyDonation(donation.id, 'rejected')}
                        size="small"
                        color="error"
                      >
                        <LogoutIcon />
                      </IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderAnnouncements = () => {
    return (
      <Grid container spacing={3}>
        {announcements.map((announcement) => (
          <Grid item xs={12} md={6} lg={4} key={announcement.id}>
            <Paper sx={styles.card}>
              <Typography variant="h6" gutterBottom>
                {announcement.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                {announcement.content}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Chip
                  label={announcement.visibility === 'core' ? 'Core Committee' : 'Everyone'}
                  color={announcement.visibility === 'core' ? 'primary' : 'default'}
                  size="small"
                />
                <Chip
                  label={new Date(announcement.created_at).toLocaleDateString()}
                  size="small"
                />
              </Stack>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button size="small" onClick={() => handleEditAnnouncement(announcement)}>
                  Edit
                </Button>
                <Button size="small" color="error" onClick={() => handleDeleteAnnouncement(announcement.id)}>
                  Delete
                </Button>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    );
  };

  const handleCreateAnnouncement = () => {
    setFormData({
      title: '',
      content: '',
      visibility: 'all',
      core_members: []
    });
    setEditAnnouncement(null);
    setOpenDialog('announcement');
  };

  // --- Finances CRUD logic ---
  // Expenses (spendings)
  const handleCreateExpense = () => {
    setSpendingForm({
      amount: '',
      description: '',
      category: '',
      date: new Date().toISOString().split('T')[0],
      reference_number: '',
      type: 'outflow',
      reason: ''
    });
    setEditingSpending(null);
    setOpenDialog('spending');
  };

  const handleEditExpense = (spending) => {
    setSpendingForm({
      amount: spending.amount.toString(),
      description: spending.description,
      category: spending.category,
      date: spending.date,
      reference_number: spending.reference_number || '',
      type: spending.type || 'outflow',
      reason: spending.reason || ''
    });
    setEditingSpending(spending.id);
    setOpenDialog('spending');
  };

  const handleDeleteExpense = handleDeleteSpending;

  // --- Finances Table Renderers ---
  const renderExpensesTable = () => (
    <TableContainer component={Paper} sx={{ borderRadius: '12px', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)' }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Amount</TableCell>
            <TableCell>Description</TableCell>
            <TableCell>Category</TableCell>
            <TableCell>Date</TableCell>
            <TableCell>Reference</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {spendings.map((spending) => (
            <TableRow key={spending.id}>
              <TableCell>₹{spending.amount}</TableCell>
              <TableCell>{spending.description}</TableCell>
              <TableCell>{spending.category}</TableCell>
              <TableCell>{new Date(spending.date).toLocaleDateString()}</TableCell>
              <TableCell>{spending.reference_number}</TableCell>
              <TableCell>
                <IconButton onClick={() => handleEditExpense(spending)} size="small"><EditIcon /></IconButton>
                <IconButton onClick={() => handleDeleteExpense(spending.id)} size="small" color="error"><DashboardIcon /></IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderFinances = () => (
    <Box sx={{ p: 0 }}>
      <Box sx={styles.sectionHeader}>
        <Typography variant="h5">Finances</Typography>
      </Box>
      <Grid container spacing={3} sx={{ px: { xs: '16px', md: '24px' }, mb: 4}}>
        <Grid item xs={12} md={6}>
            <Box sx={{
                p: 3,
                borderRadius: '16px',
                color: 'white',
                background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.85), rgba(69, 160, 73, 0.75))',
                backdropFilter: 'blur(8px)',
                boxShadow: '0 8px 32px rgba(76, 175, 80, 0.3)',
                transition: 'all 0.3s ease',
                '&:hover': { transform: 'translateY(-4px)' }
            }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Total Inflow</Typography>
                <Typography variant="h3" sx={{ mb: 1 }}>
                ₹{financeStats.inflow.toLocaleString()}
                </Typography>
            </Box>
        </Grid>
        <Grid item xs={12} md={6}>
             <Box sx={{
                p: 3,
                borderRadius: '16px',
                color: 'white',
                background: 'linear-gradient(135deg, rgba(233, 30, 99, 0.85), rgba(216, 27, 96, 0.75))',
                backdropFilter: 'blur(8px)',
                boxShadow: '0 8px 32px rgba(233, 30, 99, 0.3)',
                transition: 'all 0.3s ease',
                '&:hover': { transform: 'translateY(-4px)' }
            }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Total Outflow</Typography>
                <Typography variant="h3" sx={{ mb: 1 }}>
                ₹{financeStats.outflow.toLocaleString()}
                </Typography>
            </Box>
        </Grid>
      </Grid>
      <Box sx={{ px: { xs: '16px', md: '24px' } }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateExpense}
          >
            Add Record
          </Button>
        </Box>
        {renderExpensesTable()}
      </Box>
    </Box>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'members':
        return (
          <Box sx={{ p: 0 }}>
            <Box sx={styles.sectionHeader}>
              <Typography variant="h5">Members Management</Typography>
            </Box>
            <Box sx={{ px: { xs: '16px', md: '24px' } }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    setFormData({
                      username: '',
                      full_name: '',
                      email: '',
                      role: 'member'
                    });
                    setEditingUser(null);
                    setOpenDialog('user');
                  }}
                >
                  Add Member
                </Button>
              </Box>
              {renderUsers()}
            </Box>
          </Box>
        );

      case 'events':
        return (
          <Box sx={{ p: 0 }}>
            <Box sx={styles.sectionHeader}>
              <Typography variant="h5">Events Management</Typography>
            </Box>
            <Box sx={{ px: { xs: '16px', md: '24px' } }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleCreateEvent}
                >
                  Create Event
                </Button>
              </Box>
              {renderEvents()}
            </Box>
          </Box>
        );

      case 'donations':
        return (
          <Box sx={{ p: 0 }}>
            <Box sx={styles.sectionHeader}>
              <Typography variant="h5">Donations Management</Typography>
            </Box>
            <Box sx={{ px: { xs: '16px', md: '24px' } }}>
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={6} lg={3}>
                  <Box sx={{
                    p: 3,
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.85), rgba(69, 160, 73, 0.75))',
                    backdropFilter: 'blur(8px)',
                    boxShadow: '0 8px 32px rgba(76, 175, 80, 0.3)',
                    transition: 'all 0.3s ease',
                    '&:hover': { transform: 'translateY(-4px)' }
                  }}>
                    <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>Total Donations</Typography>
                    <Typography variant="h3" sx={{ color: 'white', mb: 1 }}>
                      ₹{donationStats.totalDonated.toLocaleString()}
                    </Typography>
                    <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      {donationStats.verifiedCount} verified donations
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                  <Box sx={{
                    p: 3,
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.85), rgba(251, 140, 0, 0.75))',
                    backdropFilter: 'blur(8px)',
                    boxShadow: '0 8px 32px rgba(255, 152, 0, 0.3)',
                    transition: 'all 0.3s ease',
                    '&:hover': { transform: 'translateY(-4px)' }
                  }}>
                    <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>Pending Donations</Typography>
                    <Typography variant="h3" sx={{ color: 'white', mb: 1 }}>
                      ₹{donationStats.totalPending.toLocaleString()}
                    </Typography>
                    <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      {donationStats.pendingCount} pending approvals
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
              {renderDonations()}
            </Box>
          </Box>
        );

      case 'announcements':
        return (
          <Box sx={{ p: 0 }}>
            <Box sx={styles.sectionHeader}>
              <Typography variant="h5">Announcements Management</Typography>
            </Box>
            <Box sx={{ px: { xs: '16px', md: '24px' } }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleCreateAnnouncement}
                >
                  Create Announcement
                </Button>
              </Box>
              {renderAnnouncements()}
            </Box>
          </Box>
        );

      case 'finances':
        return renderFinances();

      default:
        return (
          <Box sx={{ p: 0 }}>
            <Box sx={styles.sectionHeader}>
              <Typography variant="h5">Dashboard Overview</Typography>
            </Box>
            <Box sx={{ px: { xs: '16px', md: '24px' } }}>
              <Grid container spacing={3} sx={{ mb: 4 }}>
                {/* Stats Overview */}
                <Grid item xs={12} md={6} lg={3}>
                  <Box sx={{
                    p: 3,
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.85), rgba(69, 160, 73, 0.75))',
                    backdropFilter: 'blur(8px)',
                    boxShadow: '0 8px 32px rgba(76, 175, 80, 0.3)',
                    transition: 'all 0.3s ease',
                    '&:hover': { transform: 'translateY(-4px)' }
                  }}>
                    <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>Total Donations</Typography>
                    <Typography variant="h3" sx={{ color: 'white', mb: 1 }}>
                      ₹{donationStats.totalDonated.toLocaleString()}
                    </Typography>
                    <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      {donationStats.verifiedCount} verified donations
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6} lg={3}>
                  <Box sx={{
                    p: 3,
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.85), rgba(251, 140, 0, 0.75))',
                    backdropFilter: 'blur(8px)',
                    boxShadow: '0 8px 32px rgba(255, 152, 0, 0.3)',
                    transition: 'all 0.3s ease',
                    '&:hover': { transform: 'translateY(-4px)' }
                  }}>
                    <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>Pending Donations</Typography>
                    <Typography variant="h3" sx={{ color: 'white', mb: 1 }}>
                      ₹{donationStats.totalPending.toLocaleString()}
                    </Typography>
                    <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      {donationStats.pendingCount} pending approvals
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6} lg={3}>
                  <Box sx={{
                    p: 3,
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, rgba(233, 30, 99, 0.85), rgba(216, 27, 96, 0.75))',
                    backdropFilter: 'blur(8px)',
                    boxShadow: '0 8px 32px rgba(233, 30, 99, 0.3)',
                    transition: 'all 0.3s ease',
                    '&:hover': { transform: 'translateY(-4px)' }
                  }}>
                    <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>Total Expenses</Typography>
                    <Typography variant="h3" sx={{ color: 'white', mb: 1 }}>
                      ₹{spendingStats.totalSpent.toLocaleString()}
                    </Typography>
                    <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Balance: ₹{(donationStats.totalDonated - spendingStats.totalSpent).toLocaleString()}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6} lg={3}>
                  <Box sx={{
                    p: 3,
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.85), rgba(30, 136, 229, 0.75))',
                    backdropFilter: 'blur(8px)',
                    boxShadow: '0 8px 32px rgba(33, 150, 243, 0.3)',
                    transition: 'all 0.3s ease',
                    '&:hover': { transform: 'translateY(-4px)' }
                  }}>
                    <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>Total Members</Typography>
                    <Typography variant="h3" sx={{ color: 'white', mb: 1 }}>
                      {users.length}
                    </Typography>
                    <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Active members
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Recent Activity Sections */}
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Paper sx={{ p: 3, borderRadius: '16px', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)' }}>
                    <Typography variant="h6" sx={{ mb: 3 }}>Recent Donations</Typography>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Amount</TableCell>
                            <TableCell>Donor</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Date</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {donations.slice(0, 5).map((donation) => (
                            <TableRow key={donation.id}>
                              <TableCell>₹{donation.amount}</TableCell>
                              <TableCell>{donation.created_by}</TableCell>
                              <TableCell>
                                <Chip
                                  label={donation.status}
                                  color={
                                    donation.status === 'verified' ? 'success' :
                                    donation.status === 'rejected' ? 'error' :
                                    'warning'
                                  }
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>{new Date(donation.created_at).toLocaleDateString()}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          </Box>
        );
    }
  };

  return (
    <Container maxWidth={false} disableGutters sx={styles.container}>
      {/* Sidebar */}
      <Box sx={styles.sidebar}>
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <img 
            src={logo}
            alt="Ikshana Logo"
            style={{ 
              width: '40px',
              height: '40px',
              objectFit: 'contain',
              display: 'block'
            }}
          />
          <Typography variant="h6" sx={{ 
            color: 'primary.main', 
            fontWeight: 600,
            fontSize: '1.25rem',
            letterSpacing: '0.5px'
          }}>
            Admin Panel
          </Typography>
        </Box>

        {navigationItems.map((item) => (
          <Box
            key={item.id}
            sx={{
              ...styles.sidebarItem,
              backgroundColor: activeSection === item.id ? 'rgba(0, 102, 204, 0.05)' : 'transparent',
              color: activeSection === item.id ? '#0066cc' : '#1d1d1f',
            }}
            onClick={() => setActiveSection(item.id)}
          >
            {React.cloneElement(item.icon, { sx: { mr: 2 } })}
            <Typography>{item.text}</Typography>
          </Box>
        ))}

        <Box sx={{ mt: 'auto', mb: 2 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={() => {
              localStorage.removeItem('token');
              navigate('/login');
            }}
            startIcon={<LogoutIcon />}
            sx={{
              mt: 2,
              backgroundColor: '#f44336',
              color: 'white',
              '&:hover': {
                backgroundColor: '#d32f2f'
              },
              textTransform: 'none',
              borderRadius: '12px',
              py: 1
            }}
          >
            Logout
          </Button>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={styles.mainContent}>
        {renderContent()}
      </Box>

      {/* Dialogs */}
      <Dialog open={openDialog === 'user'} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editingUser ? 'Edit User' : 'Create User'}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            name="username"
            label="Username"
            type="text"
            fullWidth
            required
            value={formData.username || ''}
            onChange={handleInputChange}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="full_name"
            label="Full Name"
            type="text"
            fullWidth
            required
            value={formData.full_name || ''}
            onChange={handleInputChange}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="email"
            label="Email"
            type="email"
            fullWidth
            required
            value={formData.email || ''}
            onChange={handleInputChange}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Role</InputLabel>
            <Select
              name="role"
              value={formData.role || 'member'}
              onChange={handleInputChange}
              required
            >
              <MenuItem value="member">Member</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </Select>
          </FormControl>
          {!editingUser && (
            <TextField
              margin="dense"
              name="password"
              label="Password"
              type="password"
              fullWidth
              required
              value={formData.password || ''}
              onChange={handleInputChange}
              sx={{ mb: 2 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button 
            onClick={handleUserSubmit}
            variant="contained" 
            color="primary"
            disabled={!isUserFormValid()}
          >
            {editingUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDialog === 'announcement'} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editAnnouncement ? 'Edit Announcement' : 'Create Announcement'}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            name="title"
            label="Title"
            type="text"
            fullWidth
            required
            value={formData.title || ''}
            onChange={handleInputChange}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="content"
            label="Content"
            type="text"
            fullWidth
            multiline
            rows={4}
            required
            value={formData.content || ''}
            onChange={handleInputChange}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Visibility</InputLabel>
            <Select
              name="visibility"
              value={formData.visibility || 'all'}
              onChange={handleInputChange}
              required
            >
              <MenuItem value="all">Everyone</MenuItem>
              <MenuItem value="core">Core Committee Only</MenuItem>
            </Select>
          </FormControl>
          {formData.visibility === 'core' && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Core Committee Members</InputLabel>
              <Select
                name="core_members"
                multiple
                value={formData.core_members || []}
                onChange={handleInputChange}
                required
                renderValue={(selected) => selected.join(', ')}
              >
                {users
                  .filter(user => user.role === 'member')
                  .map(user => (
                    <MenuItem key={user.username} value={user.username}>
                      <Checkbox checked={(formData.core_members || []).indexOf(user.username) > -1} />
                      <ListItemText primary={`${user.full_name} (${user.username})`} />
                    </MenuItem>
                  ))
                }
              </Select>
              <FormHelperText>Select members who can see this announcement</FormHelperText>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button 
            onClick={handleSubmit}
            variant="contained" 
            color="primary"
            disabled={!isAnnouncementFormValid()}
          >
            {editAnnouncement ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDialog === 'donation'} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editingDonation ? 'Edit Donation' : 'Create Donation'}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Username"
              value={formData.username || ''}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Amount"
              type="number"
              value={formData.amount || ''}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Reference Number"
              value={formData.reference_number || ''}
              onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
              margin="normal"
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status || ''}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                label="Status"
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="verified">Verified</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={4}
              value={formData.notes || ''}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              margin="normal"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button 
            onClick={editingDonation ? handleUpdateDonation : handleCreateDonation}
            disabled={!isDonationFormValid()}
            variant="contained" 
            color="primary"
          >
            {editingDonation ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDialog === 'spending'} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editingSpending ? 'Edit Record' : 'Add Record'}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            name="amount"
            label="Amount"
            type="number"
            fullWidth
            required
            value={spendingForm.amount}
            onChange={(e) => setSpendingForm(prev => ({ ...prev, amount: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Type</InputLabel>
            <Select
              name="type"
              value={spendingForm.type}
              onChange={(e) => setSpendingForm(prev => ({ ...prev, type: e.target.value }))}
              required
            >
              <MenuItem value="inflow">Inflow</MenuItem>
              <MenuItem value="outflow">Outflow</MenuItem>
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            name="reason"
            label="Reason"
            type="text"
            fullWidth
            required
            value={spendingForm.reason}
            onChange={(e) => setSpendingForm(prev => ({ ...prev, reason: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="description"
            label="Description"
            type="text"
            fullWidth
            multiline
            rows={3}
            required
            value={spendingForm.description}
            onChange={(e) => setSpendingForm(prev => ({ ...prev, description: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Category</InputLabel>
            <Select
              name="category"
              value={spendingForm.category}
              onChange={(e) => setSpendingForm(prev => ({ ...prev, category: e.target.value }))}
              required
            >
              <MenuItem value="Food">Food</MenuItem>
              <MenuItem value="Transportation">Transportation</MenuItem>
              <MenuItem value="Supplies">Supplies</MenuItem>
              <MenuItem value="Equipment">Equipment</MenuItem>
              <MenuItem value="Maintenance">Maintenance</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            name="date"
            label="Date"
            type="date"
            fullWidth
            required
            value={spendingForm.date}
            onChange={(e) => setSpendingForm(prev => ({ ...prev, date: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="reference_number"
            label="Reference Number"
            type="text"
            fullWidth
            value={spendingForm.reference_number}
            onChange={(e) => setSpendingForm(prev => ({ ...prev, reference_number: e.target.value }))}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button 
            onClick={handleSubmitSpending}
            variant="contained" 
            color="primary"
            disabled={!isSpendingFormValid()}
          >
            {editingSpending ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDateRangeDialog} onClose={() => setOpenDateRangeDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Select Date Range</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              label="Start Date"
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
              fullWidth
              sx={{ mb: 2 }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Date"
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDateRangeDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleDateRangeSubmit}
            variant="contained" 
            color="primary"
            disabled={!dateRange.start || !dateRange.end}
          >
            Generate Report
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDialog === 'event'} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editEvent ? 'Edit Event' : 'Create Event'}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            name="title"
            label="Title"
            type="text"
            fullWidth
            required
            value={formData.title || ''}
            onChange={handleInputChange}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="description"
            label="Description"
            type="text"
            fullWidth
            multiline
            rows={3}
            required
            value={formData.description || ''}
            onChange={handleInputChange}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="date"
            label="Date"
            type="date"
            fullWidth
            required
            value={formData.date || ''}
            onChange={handleInputChange}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="time"
            label="Time"
            type="time"
            fullWidth
            required
            value={formData.time || ''}
            onChange={handleInputChange}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="location"
            label="Location"
            type="text"
            fullWidth
            required
            value={formData.location || ''}
            onChange={handleInputChange}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button 
            onClick={handleEventSubmit}
            variant="contained" 
            color="primary"
            disabled={!isEventFormValid()}
          >
            {editEvent ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage('')}
        message={successMessage}
      />
    </Container>
  );
};

export default AdminDashboard;
